import React, { useEffect, useMemo, useState } from "react";
import { getKapasitetsOverstyringer } from "../../lib/tjenestekatalogApi";
import {
  normalizeWorkshopSlug,
  isoDate,
  weekStartISO,
  getWeekNumber,
  prettifyStoreName,
  tidsstyrtVerdi,
  ALL_STORES,
  DAGSMÅL_PER_BUTIKK,
  addDays,
  fmtKr,
  isDateClosed,
} from "./workshopCapacityUtils";
import WorkshopDagDetaljerModal from "./WorkshopDagDetaljerModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DAY_NAMES = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];

export default function WorkshopOmsetningWeekTab({ storeSlug }) {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [overrides, setOverrides] = useState([]);
  const [modal, setModal] = useState({ open: false, title: "", services: [] });

  const isAlle = storeSlug === "alle";

  const today = useMemo(() => new Date(), []);
  const weekMonday = useMemo(() => addDays(new Date(weekStartISO(today)), weekOffset * 7), [today, weekOffset]);
  const weekSunday = useMemo(() => addDays(weekMonday, 6), [weekMonday]);
  const weekISO = useMemo(() => isoDate(weekMonday), [weekMonday]);
  const isCurrentWeek = weekOffset === 0;
  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = addDays(weekMonday, i);
        return { iso: isoDate(d), label: DAY_NAMES[i], date: d };
      }),
    [weekMonday]
  );
  const weekNo = useMemo(() => getWeekNumber(weekMonday), [weekMonday]);
  const todayISO = useMemo(() => isoDate(today), [today]);
  const cutoffISO = useMemo(() => isCurrentWeek ? todayISO : isoDate(weekSunday), [isCurrentWeek, todayISO, weekSunday]);

  const { ukesmål, dagsmål } = useMemo(() => {
    if (storeSlug === "alle") {
      const totalUke = ALL_STORES.reduce((sum, slug) => {
        const base = DAGSMÅL_PER_BUTIKK[slug] || 0;
        if (base === 0) return sum;
        const openCount = weekDays.slice(0, 5).filter(
          (wd) => !isDateClosed(overrides, slug, wd.iso, weekISO)
        ).length;
        return sum + base * openCount;
      }, 0);
      const totalDag = ALL_STORES.reduce((s, k) => s + (DAGSMÅL_PER_BUTIKK[k] || 0), 0);
      return { ukesmål: totalUke, dagsmål: totalDag };
    }
    const slug = normalizeWorkshopSlug(storeSlug);
    const base = DAGSMÅL_PER_BUTIKK[slug] || 0;
    const openCount = weekDays.slice(0, 5).filter(
      (wd) => !isDateClosed(overrides, slug, wd.iso, weekISO)
    ).length;
    return { ukesmål: base * openCount, dagsmål: base };
  }, [storeSlug, overrides, weekDays, weekISO]);

  useEffect(() => {
    getKapasitetsOverstyringer()
      .then((result) => setOverrides(result || []))
      .catch(() => setOverrides([]));
  }, []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const fromISO = isoDate(weekMonday);
        const toISO = isoDate(weekSunday);
        const slugs = isAlle ? ALL_STORES : [normalizeWorkshopSlug(storeSlug)];

        const responses = await Promise.all(
          slugs.map((slug) =>
            fetch(
              `${API_BASE_URL}/workshop/capacity-history?verkstedSlug=${encodeURIComponent(slug)}&from=${fromISO}&to=${toISO}`
            ).then((r) => (r.ok ? r.json() : { details: [] }))
          )
        );

        setDetails(responses.flatMap((r) => (r.details || []).filter((d) => !d.reason)));
      } catch (err) {
        console.error("Feil ved henting av ukesomsetning:", err);
        setDetails([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [storeSlug, isAlle, weekMonday, weekSunday]);

  // Dag-for-dag (man–lør) omsetning og akkumulert
  const allDayRows = useMemo(() => {
    let cum = 0;
    const rows = weekDays.slice(0, 6).map((wd, i) => {
      const isSaturday = i === 5;
      let isClosed = false;
      let dayDagsmål = 0;
      let missedMål = 0;
      if (!isSaturday) {
        if (storeSlug === "alle") {
          isClosed = ALL_STORES.every((slug) => isDateClosed(overrides, slug, wd.iso, weekISO));
          dayDagsmål = ALL_STORES.reduce((sum, slug) => {
            if (isDateClosed(overrides, slug, wd.iso, weekISO)) return sum;
            return sum + (DAGSMÅL_PER_BUTIKK[slug] || 0);
          }, 0);
          missedMål = ALL_STORES.reduce((sum, slug) =>
            sum + (isDateClosed(overrides, slug, wd.iso, weekISO) ? (DAGSMÅL_PER_BUTIKK[slug] || 0) : 0), 0);
        } else {
          const slug = normalizeWorkshopSlug(storeSlug);
          isClosed = isDateClosed(overrides, slug, wd.iso, weekISO);
          dayDagsmål = isClosed ? 0 : (DAGSMÅL_PER_BUTIKK[slug] || 0);
          missedMål = isClosed ? (DAGSMÅL_PER_BUTIKK[slug] || 0) : 0;
        }
      }
      const dayServices = details.filter((d) => d.ferdigDato === wd.iso);
      const dayArbeidskost = dayServices.reduce((s, d) => s + (d.omsetning || 0), 0);
      const dayTidsstyrtMin = dayServices.reduce((s, d) => s + (d.tidsstyrtMin || 0), 0);
      const dayTidsstyrtVerdi = tidsstyrtVerdi(dayTidsstyrtMin);
      const dayOms = dayArbeidskost + dayTidsstyrtVerdi;
      const antall = dayServices.length;
      const isPast = wd.iso <= cutoffISO;
      const isToday = isCurrentWeek && wd.iso === todayISO;
      if (isPast) cum += dayOms;
      const cumAtDay = isPast ? cum : null;
      const dagPct = dayDagsmål > 0 && isPast ? Math.min(200, Math.round((dayOms / dayDagsmål) * 100)) : null;
      return { ...wd, dayOms, dayArbeidskost, dayTidsstyrtVerdi, antall, cumAtDay, isPast, isToday, dagPct, isClosed, dayDagsmål, missedMål, isSaturday };
    });
    return rows;
  }, [weekDays, details, cutoffISO, todayISO, isCurrentWeek, overrides, storeSlug, weekISO]);

  // Lørdag vises i tabellen kun dersom det er servicer avsluttet den dagen
  const dayRows = useMemo(
    () => allDayRows.filter((row) => !row.isSaturday || row.antall > 0),
    [allDayRows]
  );

  // Ukedager (man–fre) — grunnlaget for et pålitelig dagssnitt, uavhengig av
  // om lørdag hadde omsetning eller ikke.
  const weekdayRows = useMemo(
    () => allDayRows.filter((row) => !row.isSaturday),
    [allDayRows]
  );
  const serviceDaysElapsed = useMemo(
    () => weekdayRows.filter((d) => d.isPast && !d.isClosed).length,
    [weekdayRows]
  );

  const totalSoFar = dayRows.filter((d) => d.isPast).reduce((s, d) => s + d.dayOms, 0);
  const remaining = Math.max(0, ukesmål - totalSoFar);
  const pctOfUkesmål = ukesmål > 0 ? Math.min(100, Math.round((totalSoFar / ukesmål) * 100)) : 0;
  const goalReached = ukesmål > 0 && totalSoFar >= ukesmål;

  // Projeksjon: når nås ukesmålet basert på snittet så langt?
  // Snittet regnes pr. faktisk servicedag (ukedag som ikke er stengt) — ikke
  // bare dager med registrert omsetning, som ga et for optimistisk snitt.
  const projeksjon = useMemo(() => {
    if (!ukesmål || goalReached) return null;
    if (serviceDaysElapsed === 0 || totalSoFar === 0) return null;
    const avgPerDay = totalSoFar / serviceDaysElapsed;
    if (avgPerDay <= 0) return null;
    const daysNeeded = Math.ceil(remaining / avgPerDay);

    // Gå fremover dag for dag gjennom de faktiske gjenstående servicedagene
    // (hopper naturlig over søndag og stengte dager) i stedet for å legge
    // til N kalenderdager blindt.
    const upcomingOpenDays = weekdayRows.filter((d) => !d.isPast && !d.isClosed);
    if (daysNeeded > upcomingOpenDays.length) {
      return { projISO: null, projLabel: "", projDate: null, projInThisWeek: false };
    }
    const projDate = upcomingOpenDays[daysNeeded - 1].date;
    const projDayIdx = (projDate.getDay() + 6) % 7;
    const projLabel = DAY_NAMES[projDayIdx] || "";
    return { projISO: isoDate(projDate), projLabel, projDate, projInThisWeek: true };
  }, [ukesmål, goalReached, serviceDaysElapsed, totalSoFar, remaining, weekdayRows]);

  const totalMissedMål = useMemo(
    () => dayRows.reduce((s, d) => s + (d.missedMål || 0), 0),
    [dayRows]
  );

  const totalTidsstyrtVerdi = useMemo(
    () => dayRows.filter((d) => d.isPast).reduce((s, d) => s + (d.dayTidsstyrtVerdi || 0), 0),
    [dayRows]
  );

  const totalArbeidskost = useMemo(
    () => dayRows.filter((d) => d.isPast).reduce((s, d) => s + (d.dayArbeidskost || 0), 0),
    [dayRows]
  );

  function openDayModal(row) {
    const services = details.filter((d) => d.ferdigDato === row.iso);
    setModal({
      open: true,
      title: `${row.label} ${row.iso} — ${fmtKr(row.dayOms)}`,
      services,
    });
  }

  function projeksjonLabel() {
    if (!ukesmål) return "Ingen omsetningsmål satt";
    if (goalReached) return "Omsetningsmålet er nådd!";
    if (!projeksjon) return serviceDaysElapsed === 0 ? "Ingen omsetning registrert ennå" : "Ikke nok data";
    if (projeksjon.projInThisWeek)
      return `Estimert nådd: ${projeksjon.projLabel} ${projeksjon.projDate.getDate()}.${String(projeksjon.projDate.getMonth() + 1).padStart(2, "0")}`;
    return "Omsetningsmålet nås ikke denne uken";
  }

  if (loading) return <div className="py-10 text-center">Laster omsetningsdata…</div>;

  return (
    <div className="space-y-6">
      <WorkshopDagDetaljerModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        title={modal.title}
        services={modal.services}
        showButikk={isAlle}
      />

      {/* Header med ukenavigasjon */}
      <div className="rounded border bg-green-50 p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm text-gray-600">Ukesomsetning for</div>
            <div className="text-xl font-bold">
              {isAlle ? "Alle verksteder" : prettifyStoreName(storeSlug)}
            </div>
            <div className="text-sm text-gray-700 mt-1">
              Uke {weekNo} ({isoDate(weekMonday)} – {isoDate(weekSunday)}) — dag for dag mot omsetningsmål ({ukesmål > 0 ? `${ukesmål.toLocaleString("nb-NO")} kr` : "ikke satt"})
              {dagsmål > 0 && ` · ${dagsmål.toLocaleString("nb-NO")} kr/dag`}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setWeekOffset((o) => o - 1)}
              className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm"
            >
              ← Forrige
            </button>
            {!isCurrentWeek && (
              <button
                onClick={() => setWeekOffset(0)}
                className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm text-green-700"
              >
                Denne uken
              </button>
            )}
            <button
              onClick={() => setWeekOffset((o) => Math.min(0, o + 1))}
              disabled={isCurrentWeek}
              className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Neste →
            </button>
          </div>
        </div>
      </div>

      {/* KPI-kort */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded border p-4 bg-white">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Omsetningsmål</div>
          <div className="text-2xl font-bold mt-1">
            {ukesmål > 0 ? `${ukesmål.toLocaleString("nb-NO")} kr` : "—"}
          </div>
          <div className="text-xs text-gray-400 mt-1">{dagsmål > 0 ? `${dagsmål.toLocaleString("nb-NO")} kr/dag` : ""}</div>
        </div>
        <div className="rounded border p-4 bg-white">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Omsetning hittil</div>
          <div className={`text-2xl font-bold mt-1 ${goalReached ? "text-green-600" : ""}`}>
            {totalSoFar > 0 ? `${totalSoFar.toLocaleString("nb-NO")} kr` : "—"}
          </div>
          <div className="text-xs text-gray-400 mt-1">{pctOfUkesmål} % av mål</div>
          {(totalArbeidskost > 0 || totalTidsstyrtVerdi > 0) && (
            <div className="text-xs text-gray-500 mt-1">
              {fmtKr(totalArbeidskost)} arbeidskost
              {totalTidsstyrtVerdi > 0 && (
                <> · <span className="text-orange-600">{fmtKr(totalTidsstyrtVerdi)} tidsstyrt</span></>
              )}
            </div>
          )}
        </div>
        <div className="rounded border p-4 bg-white">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Gjenstår</div>
          <div className={`text-2xl font-bold mt-1 ${goalReached ? "text-green-600" : "text-amber-600"}`}>
            {goalReached ? "Ingen" : ukesmål > 0 ? `${remaining.toLocaleString("nb-NO")} kr` : "—"}
          </div>
          <div className="text-xs text-gray-400 mt-1">til omsetningsmål</div>
        </div>
        <div className={`rounded border p-4 ${goalReached ? "bg-green-50 border-green-200" : "bg-white"}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Fremdrift</div>
          <div className={`text-2xl font-bold mt-1 ${goalReached ? "text-green-700" : ""}`}>
            {ukesmål > 0 ? `${pctOfUkesmål} %` : "—"}
          </div>
          <div className="text-xs text-gray-400 mt-1">{projeksjonLabel()}</div>
        </div>
      </div>

      {/* Fremdriftslinje */}
      {ukesmål > 0 && (
        <div className="rounded border bg-white p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Fremdrift mot omsetningsmål</span>
            <span className={`font-semibold ${goalReached ? "text-green-700" : "text-gray-700"}`}>
              {totalSoFar.toLocaleString("nb-NO")} / {ukesmål.toLocaleString("nb-NO")} kr
            </span>
          </div>
          <div className="relative h-5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                goalReached ? "bg-green-500" : pctOfUkesmål >= 70 ? "bg-green-400" : pctOfUkesmål >= 40 ? "bg-amber-400" : "bg-red-400"
              }`}
              style={{ width: `${pctOfUkesmål}%` }}
            />
          </div>
          <div className={`text-xs font-medium ${goalReached ? "text-green-700" : projeksjon && !projeksjon.projInThisWeek ? "text-red-600" : "text-gray-600"}`}>
            {projeksjonLabel()}
          </div>
        </div>
      )}

      {/* Stengte dager — omsetningspåvirkning */}
      {totalMissedMål > 0 && dagsmål > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <span className="text-amber-500 text-base mt-0.5 shrink-0">⚠</span>
          <div>
            <div className="text-sm font-semibold text-amber-800">
              Stengte dager påvirker ukesmålet
            </div>
            <div className="text-sm text-amber-700 mt-0.5">
              Ukesmålet er justert ned med{" "}
              <strong>{totalMissedMål.toLocaleString("nb-NO")} kr</strong>{" "}
              pga stengte dager — fra{" "}
              {(ukesmål + totalMissedMål).toLocaleString("nb-NO")} kr til{" "}
              {ukesmål.toLocaleString("nb-NO")} kr.
            </div>
          </div>
        </div>
      )}

      {/* Dag-for-dag tabell */}
      <div className="overflow-x-auto">
        <table className="min-w-full border rounded bg-white text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Dag</th>
              <th className="px-4 py-2 text-left">Dato</th>
              <th className="px-4 py-2 text-right">Arbeidskost</th>
              <th className="px-4 py-2 text-right">Tidsstyrt</th>
              <th className="px-4 py-2 text-right">Totalt</th>
              <th className="px-4 py-2 text-right">Antall</th>
              <th className="px-4 py-2 text-right">Akkumulert</th>
              <th className="px-4 py-2 text-right">Mot mål</th>
              <th className="px-4 py-2 min-w-[160px]"></th>
            </tr>
          </thead>
          <tbody>
            {dayRows.map((row) => {
              const cumPct = ukesmål > 0 && row.cumAtDay != null
                ? Math.min(100, Math.round((row.cumAtDay / ukesmål) * 100))
                : null;
              const cumReached = row.cumAtDay != null && ukesmål > 0 && row.cumAtDay >= ukesmål;

              const clickable = row.isPast && row.antall > 0;

              return (
                <tr
                  key={row.iso}
                  onClick={clickable ? () => openDayModal(row) : undefined}
                  title={clickable ? "Klikk for servicedetaljer" : undefined}
                  className={`border-t ${clickable ? "cursor-pointer" : ""} ${
                    row.isClosed ? "bg-gray-50 text-gray-400"
                    : row.isToday ? "bg-green-50 font-semibold"
                    : !row.isPast ? "text-gray-400"
                    : "hover:bg-blue-50"
                  }`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    {row.label}
                    {row.isToday && (
                      <span className="ml-2 text-xs font-normal text-green-700 bg-green-100 px-1.5 py-0.5 rounded">i dag</span>
                    )}
                    {row.isClosed && (
                      <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">Stengt</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{row.iso}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {row.isPast ? fmtKr(row.dayArbeidskost) : "–"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-orange-700">
                    {row.isPast && row.dayTidsstyrtVerdi > 0 ? fmtKr(row.dayTidsstyrtVerdi) : "–"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {row.isPast ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className={`font-medium ${row.dagPct != null && row.dagPct >= 100 ? "text-green-700" : row.dagPct != null && row.dagPct < 70 && row.dayOms > 0 ? "text-amber-700" : ""}`}>
                          {fmtKr(row.dayOms)}
                        </span>
                        {row.missedMål > 0 && (
                          <span className="text-xs text-amber-600 font-medium">
                            −{row.missedMål.toLocaleString("nb-NO")} kr mål
                          </span>
                        )}
                      </div>
                    ) : "–"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {row.isPast ? row.antall || "0" : "–"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-medium">
                    {row.cumAtDay != null ? `${row.cumAtDay.toLocaleString("nb-NO")} kr` : "–"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {cumPct != null ? (
                      <span className={`font-medium ${cumReached ? "text-green-700" : ""}`}>
                        {cumPct} %
                      </span>
                    ) : "–"}
                  </td>
                  <td className="px-4 py-3">
                    {cumPct != null && (
                      <div className="flex items-center gap-2">
                        <div className="relative h-3 flex-1 rounded-full bg-gray-100 overflow-hidden min-w-[100px]">
                          <div
                            className={`h-full rounded-full ${cumReached ? "bg-green-500" : cumPct >= 70 ? "bg-green-400" : cumPct >= 40 ? "bg-amber-400" : "bg-red-400"}`}
                            style={{ width: `${cumPct}%` }}
                          />
                        </div>
                        {dagsmål > 0 && row.dayOms > 0 && (
                          <span className={`text-xs w-14 text-right ${row.dagPct >= 100 ? "text-green-700" : row.dagPct < 70 ? "text-amber-600" : "text-gray-500"}`}>
                            {row.dagPct} % dag
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t bg-gray-50 font-semibold">
            <tr>
              <td colSpan={2} className="px-4 py-2">Totalt hittil</td>
              <td className="px-4 py-2 text-right">{fmtKr(totalArbeidskost)}</td>
              <td className="px-4 py-2 text-right text-orange-700">
                {totalTidsstyrtVerdi > 0 ? fmtKr(totalTidsstyrtVerdi) : "—"}
              </td>
              <td className="px-4 py-2 text-right">{fmtKr(totalSoFar)}</td>
              <td className="px-4 py-2 text-right">
                {dayRows.filter((d) => d.isPast).reduce((s, d) => s + d.antall, 0)}
              </td>
              <td className="px-4 py-2 text-right">{fmtKr(totalSoFar)}</td>
              <td className="px-4 py-2 text-right">
                {ukesmål > 0 ? (
                  <span className={goalReached ? "text-green-700" : ""}>
                    {pctOfUkesmål} %
                  </span>
                ) : "—"}
              </td>
              <td className="px-4 py-2" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
